import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { RoundedBox, Text } from '@react-three/drei';
import * as THREE from 'three';

export type AvenBoxScrollState = { rotY: number; rotX: number; xray: number; vent: number };

interface AvenBoxLiteProps {
    scrollState: React.RefObject<AvenBoxScrollState>;
}

const ACCENT = '#cadc76';
const { lerp } = THREE.MathUtils;

// Pre-build glow edge geometry data
const GLOW_EDGES: [number, number, number, number, number, number][] = [
    [0,     1.45,  0.045, 2.4,  0.03, 0.003],
    [0,    -1.45,  0.045, 2.4,  0.03, 0.003],
    [-1.35, 0,     0.045, 0.03, 2.7,  0.003],
    [1.35,  0,     0.045, 0.03, 2.7,  0.003],
];

const Z = {
    front:   +0.33,
    battery: +0.145,
    pcb:     +0.040,
    frame:    0.0,
    cooler:  -0.195,
    back:    -0.33,
};

export default function AvenBoxLite({ scrollState }: AvenBoxLiteProps) {
    const groupRef = useRef<THREE.Group>(null);
    const introProgress = useRef(0);

    // ── All MeshStandardMaterial — no transmission, no clearcoat ──────────

    const frontShellMat = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#1c1c1c', metalness: 0.22, roughness: 0.9,
        transparent: true, opacity: 1,
    }), []);

    const backShellMat = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#2a2a2a', metalness: 0.32, roughness: 0.85,
        transparent: true, opacity: 1,
    }), []);

    // Studio aluminum — cool blue-silver, mirror-polished so lights bloom sharp off edges
    const aluminumMat = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#bec5cc', metalness: 1.0, roughness: 0.04,
        emissive: new THREE.Color('#bec5cc'), emissiveIntensity: 0.05,
    }), []);

    // Internals — start invisible, fade in during xray
    const pcbMat = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#081c00', metalness: 0.3, roughness: 0.7,
        emissive: new THREE.Color('#061600'), emissiveIntensity: 0.5,
        transparent: true, opacity: 0,
    }), []);

    const batteryMat = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#888888', metalness: 0.6, roughness: 0.4,
        transparent: true, opacity: 0,
    }), []);

    const copperMat = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#b87333', metalness: 0.8, roughness: 0.3,
        emissive: new THREE.Color('#b87333'), emissiveIntensity: 0.12,
        transparent: true, opacity: 0,
    }), []);

    const siliconMat = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#0f0f0f', metalness: 0.5, roughness: 0.2,
        transparent: true, opacity: 0,
    }), []);

    // THE HERO MATERIAL — overbright emissive, perceived bloom without post-processing
    const accentGlowMat = useMemo(() => new THREE.MeshStandardMaterial({
        color: ACCENT,
        emissive: new THREE.Color(ACCENT),
        emissiveIntensity: 2.5,
        metalness: 0.1,
        roughness: 0.05,
    }), []);

    useFrame((state, delta) => {
        if (!groupRef.current) return;

        // Intro ease-in
        introProgress.current = Math.min(1, introProgress.current + delta * 0.5);
        const ease = 1 - Math.pow(2, -10 * introProgress.current);

        const ss = scrollState.current;
        const xr = ss.xray;
        const vent = ss.vent;

        // Gentle float
        const floatY = Math.sin(state.clock.elapsedTime * 1.4) * 0.07 * ease;
        const g = groupRef.current;

        g.position.y = lerp(g.position.y, floatY, 0.05);
        g.rotation.y = lerp(g.rotation.y, ss.rotY * Math.PI * 2, 0.055);
        g.rotation.x = lerp(g.rotation.x, ss.rotX, 0.045);

        // Shell opacity — xray dissolve (cheap: just opacity, no transmission)
        const shellTarget = xr > 0.04 ? Math.max(0.06, 1 - xr * 0.94) : 1;
        frontShellMat.opacity = lerp(frontShellMat.opacity, shellTarget, 0.07);
        backShellMat.opacity  = lerp(backShellMat.opacity,  shellTarget, 0.07);

        // Internal layers fade in as xray kicks in
        const intTarget = xr > 0.18 ? Math.min(1, (xr - 0.18) * 2.4) : 0;
        pcbMat.opacity     = lerp(pcbMat.opacity,     intTarget,        0.07);
        batteryMat.opacity = lerp(batteryMat.opacity, intTarget * 0.88, 0.07);
        copperMat.opacity  = lerp(copperMat.opacity,  intTarget * 0.82, 0.07);
        siliconMat.opacity = lerp(siliconMat.opacity, intTarget,        0.07);

        // Accent blazes during xray — 3.0 base → 7.0 at full reveal
        accentGlowMat.emissiveIntensity = lerp(
            accentGlowMat.emissiveIntensity,
            3.0 + xr * 4.0 + vent * 1.0,
            0.06,
        );
    });

    return (
        <group ref={groupRef}>

            {/* ═══ LAYER 1: FRONT SHELL ═════════════════════════════════════════ */}
            <group position={[0, 0, Z.front]}>
                <RoundedBox args={[3.65, 9.45, 0.06]} radius={0.04} smoothness={12}>
                    <primitive object={frontShellMat} attach="material" />
                </RoundedBox>

                {/* OLED Display */}
                <group position={[0, 0.8, 0.062]}>
                    {/* Bezel */}
                    <RoundedBox args={[2.5, 2.5, 0.03]} radius={0.05} smoothness={8}>
                        <primitive object={aluminumMat} attach="material" />
                    </RoundedBox>
                    {/* Panel */}
                    <RoundedBox args={[2.2, 2.2, 0.005]} radius={0.04} smoothness={8} position={[0, 0, 0.012]}>
                        <meshStandardMaterial color="#000000" metalness={0.1} roughness={0.05} />
                    </RoundedBox>
                    {/* Status ring — glowing lime */}
                    <mesh position={[0, 0.15, 0.022]} renderOrder={1}>
                        <ringGeometry args={[0.45, 0.5, 32]} />
                        <primitive object={accentGlowMat} attach="material" />
                    </mesh>
                    {/* Activity dot */}
                    <mesh position={[0, 0.15, 0.022]} renderOrder={1}>
                        <circleGeometry args={[0.12, 24]} />
                        <meshStandardMaterial
                            color={ACCENT} emissive={ACCENT} emissiveIntensity={2.0}
                            transparent depthWrite={false}
                        />
                    </mesh>
                    {/* Mode label */}
                    <Text
                        fontSize={0.12} position={[0, -0.45, 0.022]}
                        anchorX="center" letterSpacing={0.15} renderOrder={1}
                    >
                        SOLO
                        <meshBasicMaterial attach="material" color={ACCENT} depthWrite={false} transparent />
                    </Text>
                    <Text
                        fontSize={0.07} position={[0, -0.65, 0.022]}
                        anchorX="center" renderOrder={1}
                    >
                        ready
                        <meshBasicMaterial attach="material" color="#555555" depthWrite={false} transparent />
                    </Text>
                </group>

                {/* Brand — front face */}
                <Text
                    fontSize={0.35} position={[0, -2.2, 0.038]}
                    anchorX="center" letterSpacing={-0.03} renderOrder={1}
                >
                    avenbox
                    <meshBasicMaterial attach="material" color="#EBE8E2" depthWrite={false} transparent />
                </Text>
                <Text
                    fontSize={0.09} position={[0, -2.65, 0.038]}
                    anchorX="center" letterSpacing={0.08} renderOrder={1}
                >
                    SOVEREIGN AI
                    <meshBasicMaterial attach="material" color="#444444" depthWrite={false} transparent />
                </Text>
            </group>

            {/* ═══ LAYER 2: BATTERY ═════════════════════════════════════════════ */}
            <group position={[0, 0, Z.battery]}>
                <RoundedBox args={[3.2, 5.0, 0.16]} radius={0.04} smoothness={4}>
                    <primitive object={batteryMat} attach="material" />
                </RoundedBox>
            </group>

            {/* ═══ LAYER 3: AVENCORE COMPUTE MODULE ════════════════════════════ */}
            <group position={[0, 0, Z.pcb]}>
                {/* PCB substrate */}
                <RoundedBox args={[3.4, 8.8, 0.05]} radius={0.04} smoothness={4}>
                    <primitive object={pcbMat} attach="material" />
                </RoundedBox>

                {/* AvenCore sealed module */}
                <group position={[0, -0.3, 0.028]}>
                    <RoundedBox args={[2.8, 3.0, 0.08]} radius={0.02} smoothness={8}>
                        <meshStandardMaterial color="#080808" metalness={0.9} roughness={0.3} />
                    </RoundedBox>
                    {/* Heat spreader */}
                    <mesh position={[0, 0, 0.042]}>
                        <boxGeometry args={[2.6, 2.8, 0.005]} />
                        <meshStandardMaterial color="#1a1a1a" metalness={0.95} roughness={0.4} />
                    </mesh>
                    {/* Glowing accent edges — the chip's visual signature */}
                    {GLOW_EDGES.map(([x, y, z, w, h, d], i) => (
                        <mesh key={`glow-${i}`} position={[x, y, z]}>
                            <boxGeometry args={[w, h, d]} />
                            <primitive object={accentGlowMat} attach="material" />
                        </mesh>
                    ))}
                    <Text
                        fontSize={0.25} position={[0, 0.2, 0.046]}
                        anchorX="center" letterSpacing={-0.05}
                    >
                        AVENCORE
                        <meshBasicMaterial attach="material" color="#444444" depthWrite={false} transparent />
                    </Text>
                    <Text
                        fontSize={0.08} position={[0, -0.2, 0.046]}
                        anchorX="center"
                    >
                        9B PARAMETER / 102.4 GB/s
                        <meshBasicMaterial attach="material" color={ACCENT} depthWrite={false} transparent />
                    </Text>
                </group>

                {/* NVMe storage */}
                <group position={[0, 3.2, 0.027]}>
                    <RoundedBox args={[1.2, 2.0, 0.03]} radius={0.02} smoothness={4}>
                        <primitive object={siliconMat} attach="material" />
                    </RoundedBox>
                    <Text fontSize={0.12} position={[0, -0.5, 0.018]} anchorX="center">
                        512GB NVMe
                        <meshBasicMaterial attach="material" color={ACCENT} depthWrite={false} transparent />
                    </Text>
                </group>
            </group>

            {/* ═══ LAYER 4: ALUMINUM CHASSIS ════════════════════════════════════ */}
            <group position={[0, 0, Z.frame]}>
                <RoundedBox args={[3.8, 9.6, 0.7]} radius={0.08} smoothness={16}>
                    <primitive object={aluminumMat} attach="material" />
                </RoundedBox>
                {/* Antenna lines */}
                <mesh position={[0,  4.5, 0.092]}>
                    <boxGeometry args={[3.0, 0.03, 0.005]} />
                    <meshStandardMaterial color="#333333" />
                </mesh>
                <mesh position={[0, -4.5, 0.092]}>
                    <boxGeometry args={[3.0, 0.03, 0.005]} />
                    <meshStandardMaterial color="#333333" />
                </mesh>
                {/* Power / control button */}
                <mesh position={[1.86, 2.2, 0]}>
                    <boxGeometry args={[0.06, 0.7, 0.1]} />
                    <meshStandardMaterial color="#888888" metalness={0.95} roughness={0.12} />
                </mesh>
                <mesh position={[1.895, 2.2, 0]}>
                    <boxGeometry args={[0.01, 0.5, 0.06]} />
                    <meshStandardMaterial color="#555555" metalness={0.8} roughness={0.2} />
                </mesh>

                {/* Volume up */}
                <mesh position={[1.86, 0.85, 0]}>
                    <boxGeometry args={[0.06, 0.55, 0.1]} />
                    <meshStandardMaterial color="#909090" metalness={0.95} roughness={0.12} />
                </mesh>
                <mesh position={[1.895, 0.85, 0]}>
                    <boxGeometry args={[0.01, 0.38, 0.06]} />
                    <meshStandardMaterial color="#606060" metalness={0.8} roughness={0.2} />
                </mesh>

                {/* Volume down */}
                <mesh position={[1.86, 0.1, 0]}>
                    <boxGeometry args={[0.06, 0.55, 0.1]} />
                    <meshStandardMaterial color="#909090" metalness={0.95} roughness={0.12} />
                </mesh>
                <mesh position={[1.895, 0.1, 0]}>
                    <boxGeometry args={[0.01, 0.38, 0.06]} />
                    <meshStandardMaterial color="#606060" metalness={0.8} roughness={0.2} />
                </mesh>

                {/* Fingerprint reader — flush circle, accent ring */}
                <group position={[1.875, -1.4, 0]}>
                    {/* Outer accent ring */}
                    <mesh rotation={[0, Math.PI / 2, 0]}>
                        <ringGeometry args={[0.22, 0.27, 40]} />
                        <meshStandardMaterial
                            color={ACCENT} emissive={ACCENT} emissiveIntensity={1.5}
                            metalness={0.4} roughness={0.1} transparent depthWrite={false}
                        />
                    </mesh>
                    {/* Sensor face */}
                    <mesh rotation={[0, Math.PI / 2, 0]}>
                        <circleGeometry args={[0.21, 40]} />
                        <meshStandardMaterial color="#1a1a1a" metalness={0.6} roughness={0.35} />
                    </mesh>
                    {/* Subtle fingerprint ridge lines — 4 arcs approximated as thin boxes */}
                    {[-0.07, -0.02, 0.03, 0.08].map((z, i) => (
                        <mesh key={i} position={[0.002, 0, z]} rotation={[0, Math.PI / 2, 0]}>
                            <boxGeometry args={[0.001, 0.28, 0.004]} />
                            <meshStandardMaterial color="#333333" />
                        </mesh>
                    ))}
                </group>
                {/* Bottom ports — copied from original AvenBox3D */}
                <group position={[0, -4.801, 0]}>
                    {/* USB-C Port (Left) */}
                    <group position={[-0.5, 0, 0]}>
                        {/* Black void mask */}
                        <mesh position={[0, 0, 0]}>
                            <boxGeometry args={[0.31, 0.005, 0.14]} />
                            <meshBasicMaterial color="#000000" />
                        </mesh>
                        <mesh position={[-0.155, 0, 0]}>
                            <cylinderGeometry args={[0.07, 0.07, 0.005, 32]} />
                            <meshBasicMaterial color="#000000" />
                        </mesh>
                        <mesh position={[0.155, 0, 0]}>
                            <cylinderGeometry args={[0.07, 0.07, 0.005, 32]} />
                            <meshBasicMaterial color="#000000" />
                        </mesh>
                        {/* Center tongue */}
                        <mesh position={[0, -0.005, 0]}>
                            <boxGeometry args={[0.2, 0.015, 0.04]} />
                            <meshStandardMaterial color="#555" metalness={0.8} roughness={0.3} />
                        </mesh>
                    </group>
                    {/* USB-A Port (Right) */}
                    <group position={[0.5, 0, 0]}>
                        {/* Black void mask */}
                        <mesh position={[0, 0, 0]}>
                            <boxGeometry args={[0.7, 0.005, 0.25]} />
                            <meshBasicMaterial color="#000000" />
                        </mesh>
                        {/* Plastic tongue */}
                        <mesh position={[0, -0.005, -0.05]}>
                            <boxGeometry args={[0.6, 0.015, 0.1]} />
                            <meshStandardMaterial color="#111" metalness={0.3} roughness={0.8} />
                        </mesh>
                        {/* Metal contacts */}
                        <mesh position={[0, -0.013, -0.05]}>
                            <boxGeometry args={[0.5, 0.002, 0.04]} />
                            <meshStandardMaterial color="#b87333" metalness={1} roughness={0.2} />
                        </mesh>
                    </group>
                </group>
            </group>

            {/* ═══ LAYER 5: AVENCOOL THERMAL ════════════════════════════════════ */}
            <group position={[0, 0, Z.cooler]}>
                <RoundedBox args={[2.6, 3.6, 0.05]} radius={0.06} smoothness={4}>
                    <primitive object={copperMat} attach="material" />
                </RoundedBox>
                {[-1.0, -0.5, 0, 0.5, 1.0].map((y, i) => (
                    <mesh key={`fin-${i}`} position={[0, y, 0.028]}>
                        <boxGeometry args={[2.2, 0.04, 0.004]} />
                        <meshStandardMaterial color="#d4956a" metalness={0.9} roughness={0.15} />
                    </mesh>
                ))}
            </group>

            {/* ═══ LAYER 6: BACK SHELL ══════════════════════════════════════════ */}
            <group position={[0, 0, Z.back]}>
                <RoundedBox args={[3.65, 9.45, 0.06]} radius={0.04} smoothness={12}>
                    <primitive object={backShellMat} attach="material" />
                </RoundedBox>

                {/* Exhaust vents — slits glow with accentGlowMat */}
                {([0.065, -0.065] as const).map((zFace, fi) => (
                    <group key={`exhaust-${fi}`}>
                        <mesh position={[0, 4.3, zFace]}>
                            <boxGeometry args={[2.8, 0.2, 0.015]} />
                            <meshStandardMaterial color="#1a1a1a" metalness={0.5} roughness={0.6} />
                        </mesh>
                        {[-1.0, -0.5, 0, 0.5, 1.0].map((x, i) => (
                            <mesh key={`slit-${fi}-${i}`} position={[x, 4.3, zFace * 0.88]}>
                                <boxGeometry args={[0.08, 0.15, 0.003]} />
                                <primitive object={accentGlowMat} attach="material" />
                            </mesh>
                        ))}
                    </group>
                ))}

                {/* Speaker grilles */}
                {([0.065, -0.065] as const).map((zFace, fi) => (
                    <group key={`spk-${fi}`}>
                        {([-1.0, 1.0] as const).map((xPos) => (
                            <group key={`s-${fi}-${xPos}`} position={[xPos, -4.0, zFace]}>
                                {[-0.18, -0.09, 0, 0.09, 0.18].map((x, i) => (
                                    <mesh key={i} position={[x, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
                                        <cylinderGeometry args={[0.04, 0.04, 0.015, 8]} />
                                        <meshStandardMaterial color="#0a0a0a" />
                                    </mesh>
                                ))}
                            </group>
                        ))}
                    </group>
                ))}

                {/* Top exhaust bar */}
                <group position={[0, 4.75, 0]}>
                    <mesh>
                        <boxGeometry args={[2.8, 0.08, 0.13]} />
                        <meshStandardMaterial color="#111111" metalness={0.5} roughness={0.7} />
                    </mesh>
                </group>

                {/* RF antenna window */}
                <mesh position={[0, 3.5, 0.063]}>
                    <boxGeometry args={[2.8, 1.0, 0.008]} />
                    <meshStandardMaterial color="#1a1a30" metalness={0.05} roughness={0.4} transparent opacity={0.5} />
                </mesh>

                {/* Brand — back face */}
                <Text
                    fontSize={0.2}
                    position={[0, -3.5, -0.032]}
                    rotation={[0, Math.PI, 0]}
                    anchorX="center"
                    anchorY="middle"
                    letterSpacing={0.15}
                    renderOrder={1}
                >
                    AVEN STUDIOS
                    <meshStandardMaterial
                        attach="material"
                        color="#ffffff"
                        metalness={0.8}
                        roughness={0.3}
                        emissive="#222222"
                        emissiveIntensity={0.5}
                        depthWrite={false}
                    />
                </Text>
            </group>

        </group>
    );
}
